import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '../../lib/cn';
import { Spinner } from './Spinner';

type Variant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'plain' | 'danger';
type Size = 'sm' | 'md';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

const variantClasses: Record<Variant, string> = {
  primary: 'bg-action text-action-fg hover:bg-action-hover disabled:opacity-40',
  secondary:
    'bg-card text-ink-strong border border-line-input hover:bg-hover disabled:text-ink-faint',
  // A secondary button outlined in the action colour (graphite, inverted in dark) instead of grey.
  // Its own variant because `cn` only joins class names: a `className` of action colours on
  // `secondary` would sit beside the grey ones, not replace them.
  outline:
    'bg-card text-link border border-action hover:bg-tint disabled:border-line disabled:text-ink-faint',
  ghost: 'bg-transparent text-ink-label hover:bg-chip disabled:text-ink-ghost',
  // A plain text button (spec:99): no border or fill, a light background only on hover and on keyboard
  // focus (the focus ring stays), and none on a disabled one. Brand Setup's Back, Exit onboarding and
  // Skip for now, Dismiss, Undo, Remove audience and the like. `ghost` above is a different button.
  plain:
    'bg-transparent text-ink-label hover:bg-chip focus-visible:bg-chip disabled:text-ink-ghost disabled:hover:bg-transparent',
  // Stays red in both themes (a fixed fill, so its white text is right in dark too).
  danger: 'bg-red-600 text-white hover:bg-red-700 disabled:opacity-40',
};

const sizeClasses: Record<Size, string> = {
  sm: 'h-8 px-3 text-sm',
  md: 'h-10 px-4 text-sm',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading = false, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        'focus-ring inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-colors disabled:cursor-not-allowed',
        variantClasses[variant],
        sizeClasses[size],
        className,
      )}
      {...rest}
    >
      {loading && <Spinner className="h-4 w-4" />}
      {children}
    </button>
  );
});
